import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { RiUserAddLine, RiIdCardLine, RiSearchLine } from "react-icons/ri";

import {
  getAllAssociateCompany,
  removeAssociationCompany,
  updateLoginPermission,
} from "../../redux/modules/associateCompany/actions";

import { DialogContext } from "../../hooks/dialog/context";
import useNotification from "../../hooks/newUXNotification";
import { useLoading } from "../../hooks/loading";

import AssociatedProvidersTable from "./AssociatedProvidersTable";
import AssociateProviderDrawer from "./AssociateProviderDrawer";
import DeleteAssociationModal from "./Modal/DeleteAssociationModal";
import GrantExhibitorAccessModal from "./Modal/GrantExhibitorAccessModal";
import MessageDeleteAssociateCompany from "./Modal/MessageDeleteAssociateCompany";

import {
  Container,
  Title,
  Hr,
  AssociatedCard,
  AssociatedHeader,
  AssociatedTitleBox,
  AssociatedTitle,
  AssociatedSubtitle,
  PrimaryButton,
  SearchBox,
  SearchInput,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  EmptyLink,
} from "./styles";

const AssociateCompany = () => {
  const intl = useIntl();
  const { createDialog } = useContext(DialogContext);
  const dispatch = useDispatch();
  const notification = useNotification();
  const { handleLoading } = useLoading();

  const { associatedCompanies } = useSelector(
    (state) => state.associateCompany,
  );
  const { contract, event } = useSelector((state) => state.initialSettings);

  const [tablePagination, setTablePagination] = useState({
    current: 0,
    defaultCurrent: 0,
    pageSize: 10,
    showQuickJumper: true,
    total: 0,
  });

  const [search, setSearch] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const searchTimeoutRef = useRef(null);

  const handleOpenCreateDrawer = useCallback(() => {
    setEditingRecord(null);
    setIsDrawerOpen(true);
  }, []);

  const handleEditProvider = useCallback((record) => {
    setEditingRecord(record);
    setIsDrawerOpen(true);
  }, []);

  useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  const callbackPaginationInfo = useCallback(
    (pagination) => {
      handleLoading(false);
      setTablePagination({
        current: pagination.pageNumber + 1,
        defaultCurrent: 1,
        pageSize: pagination.maxSize,
        showQuickJumper: true,
        total: pagination.totalElements,
      });
    },
    [handleLoading],
  );

  const callbackError = useCallback(
    (message) => {
      handleLoading(false);
      notification.displayError(
        intl.formatMessage({
          id: "api.error." + message,
        }),
        "get-credentials-associate-error",
      );
    },
    [notification, handleLoading, intl],
  );

  const handleGetInfoTable = useCallback(
    (page = 0, pageSize = 10, search = "", sortDirectionOverride = sortDirection) => {
      handleLoading(true);
      dispatch(
        getAllAssociateCompany(
          {
            page,
            per_page: pageSize,
            opportunity_id: contract.value,
            event_id: event.value,
            search,
            sortBy: "companyName",
            sortDirection: sortDirectionOverride,
          },
          callbackPaginationInfo,
          callbackError,
        ),
      );
    },
    [
      callbackError,
      callbackPaginationInfo,
      contract.value,
      dispatch,
      event.value,
      handleLoading,
      sortDirection,
    ],
  );

  const handleSortByName = useCallback(() => {
    const nextDirection = sortDirection === "asc" ? "desc" : "asc";
    setSortDirection(nextDirection);
    handleGetInfoTable(0, tablePagination.pageSize, search, nextDirection);
  }, [sortDirection, handleGetInfoTable, tablePagination.pageSize, search]);

  const handleSearch = useCallback(
    (value) => {
      setSearch(value);
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        handleGetInfoTable(0, tablePagination.pageSize, value);
      }, 500);
    },
    [handleGetInfoTable, tablePagination.pageSize],
  );

  const handlePageChange = useCallback(
    (page, pageSize) => handleGetInfoTable(page, pageSize, search),
    [handleGetInfoTable, search],
  );

  const callbackEditPermission = useCallback(() => {
    notification.displaySuccess(
      intl.formatMessage({
        id: "success.update_association_permission",
      }),
      "update-associate-company-success",
    );
    handleGetInfoTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, intl]);

  const handleEditPermission = useCallback(
    (data) => {
      createDialog({
        id: "modal-edit-permission",
        Component: GrantExhibitorAccessModal,
        props: {
          isRemovingPermission: data.allowManageAccount,
          confirmation: () => {
            handleLoading(true);
            dispatch(
              updateLoginPermission(
                data.serviceProviderId,
                { allowManageAccount: !data.allowManageAccount },
                { event_id: event.value, opportunity_id: contract.value },
                callbackEditPermission,
                callbackError,
              ),
            );
          },
        },
      });
    },
    [
      callbackEditPermission,
      callbackError,
      contract.value,
      createDialog,
      dispatch,
      event.value,
      handleLoading,
    ],
  );

  const callbackSuccessDelete = useCallback(() => {
    notification.displaySuccess(
      intl.formatMessage({
        id: "association_deleted_successfully",
      }),
      "remove-associate-company-success",
    );
    handleGetInfoTable();
    setIsDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, intl]);

  const handleDeleteAssociateCompany = useCallback(
    (data) => {
      if (data?.hasAssociatedCredentials) {
        createDialog({
          id: "modal-message-delete",
          Component: MessageDeleteAssociateCompany,
          props: {
            title: intl.formatMessage({ id: "delete_membership" }),
            message: intl.formatMessage({
              id: "error.delete_associate_comapany",
            }),
          },
        });

        return;
      }

      createDialog({
        id: "modal-associate-company",
        Component: DeleteAssociationModal,
        props: {
          providerName: data.serviceProviderName,
          confirmation: () => {
            handleLoading(true);
            dispatch(
              removeAssociationCompany(
                data.serviceProviderId,
                { event_id: event.value, opportunity_id: contract.value },
                callbackSuccessDelete,
                callbackError,
              ),
            );
          },
        },
      });
    },
    [
      callbackError,
      callbackSuccessDelete,
      contract.value,
      createDialog,
      dispatch,
      event.value,
      intl,
    ],
  );

  useEffect(() => {
    if (!contract.value || !event.value) {
      notification.displayInfo(
        intl.formatMessage({ id: "error.contract_not_selected" }),
        "event-not-selected-message",
      );
    } else {
      handleGetInfoTable();
      notification.close("event-not-selected-message");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.value, event.value]);

  return (
    <Container>
      <Title>{intl.formatMessage({ id: "service_provider" })}</Title>
      <Hr />
      {event.value && contract.value && (
        <AssociatedCard>
          <AssociatedHeader>
            <AssociatedTitleBox>
              <AssociatedTitle>
                {intl.formatMessage({ id: "linked_service_providers" })}
              </AssociatedTitle>
              <AssociatedSubtitle>
                {intl.formatMessage({
                  id: "manage_service_providers_subtitle",
                })}
              </AssociatedSubtitle>
            </AssociatedTitleBox>

            <PrimaryButton type="button" onClick={handleOpenCreateDrawer}>
              <RiUserAddLine size={16} color="#fff" />
              {intl.formatMessage({ id: "associate_provider" })}
            </PrimaryButton>
          </AssociatedHeader>

          <SearchBox>
            <SearchInput
              prefix={<RiSearchLine size={16} color="rgba(0,0,0,0.45)" />}
              placeholder={intl.formatMessage({
                id: "search_by_name_or_document",
              })}
              allowClear
              onChange={({ target }) => handleSearch(target.value)}
            />
          </SearchBox>

          {associatedCompanies && associatedCompanies.length > 0 ? (
            <AssociatedProvidersTable
              dataSource={associatedCompanies}
              tablePagination={tablePagination}
              onPageChange={handlePageChange}
              onEdit={handleEditProvider}
              onEditPermission={handleEditPermission}
              onDelete={handleDeleteAssociateCompany}
              sortDirection={sortDirection}
              onSortByName={handleSortByName}
            />
          ) : (
            <EmptyState>
              <EmptyIcon>
                <RiIdCardLine size={24} color="#11A7D9" />
              </EmptyIcon>
              <EmptyTitle>
                {intl.formatMessage({ id: "no_provider_associated" })}
              </EmptyTitle>
              <EmptyText>
                {intl.formatMessage({
                  id: "no_provider_associated_description",
                })}
              </EmptyText>
              <EmptyLink type="button" onClick={handleOpenCreateDrawer}>
                {intl.formatMessage({ id: "associate_provider" })}
              </EmptyLink>
            </EmptyState>
          )}
        </AssociatedCard>
      )}

      <AssociateProviderDrawer
        open={isDrawerOpen}
        record={editingRecord}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={handleGetInfoTable}
        onDelete={handleDeleteAssociateCompany}
      />
    </Container>
  );
};

export default AssociateCompany;
